import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

/**
 * InlineEditorShell — shared wrapper that turns a page-local edit/new form
 * into the whole page (3CX/Vodia style), replacing the floating
 * Shadcn <Dialog> pattern.
 *
 * Props:
 *   crumb       e.g. "Trunks"  — first breadcrumb, clickable back to list
 *   title       short title shown under the breadcrumb (e.g. "Edit Trunk: gw-sipus")
 *   onCancel    fires when user clicks the breadcrumb / Cancel button
 *   onSave      fires when user clicks Save
 *   saveLabel   optional override for the Save button label
 *   cancelLabel optional override for the Cancel button label
 *   saveDisabled optional boolean — dims the Save button (validation gate)
 *   maxWidth    Tailwind class for inner card width (default `max-w-3xl`)
 *   testidRoot  test-id prefix (default `inline-editor`)
 *   children    the actual form body (fields, sections, etc.)
 *
 * Usage pattern:
 *   if (editor !== null) {
 *     return (
 *       <InlineEditorShell
 *         crumb="Trunks"
 *         title={editor === "new" ? "New trunk" : `Edit trunk: ${editor.name}`}
 *         onCancel={() => setEditor(null)}
 *         onSave={save}
 *       >
 *         <FormFields .../>
 *       </InlineEditorShell>
 *     );
 *   }
 *   return <ListView ... />;
 */
export function InlineEditorShell({
  crumb,
  crumbTail,
  title,
  onCancel,
  onSave,
  saveLabel,
  cancelLabel,
  saveDisabled = false,
  maxWidth = "max-w-3xl",
  testidRoot = "inline-editor",
  children,
}) {
  const { t } = useTranslation();
  return (
    <div data-testid={testidRoot}>
      <div
        className="sticky top-[var(--toolbar-h)] z-20 -mx-3 sm:-mx-4 md:-mx-6 px-3 sm:px-4 md:px-6 py-3 bg-background/95 backdrop-blur border-b border-border flex items-center gap-3 flex-wrap"
        data-testid={`${testidRoot}-bar`}
      >
        <nav className="text-xs text-muted-foreground flex items-center gap-1.5 flex-1 min-w-0">
          {crumb && (
            <>
              <button
                type="button"
                onClick={onCancel}
                className="hover:text-foreground transition-colors"
                data-testid={`${testidRoot}-back`}
              >
                {crumb}
              </button>
              <span>/</span>
            </>
          )}
          <span className="text-foreground font-medium truncate">
            {crumbTail || title}
          </span>
        </nav>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="ghost"
            onClick={onCancel}
            data-testid={`${testidRoot}-cancel`}
          >
            {cancelLabel || t("common.cancel", "Cancel")}
          </Button>
          <Button
            onClick={onSave}
            disabled={saveDisabled}
            data-testid={`${testidRoot}-save`}
          >
            {saveLabel || t("common.save", "Save")}
          </Button>
        </div>
      </div>
      <div className={`mx-auto ${maxWidth} py-5`}>
        {title && <h1 className="text-xl font-semibold mb-4">{title}</h1>}
        <div className="rounded-lg border border-border bg-card p-5 space-y-3">
          {children}
        </div>
      </div>
    </div>
  );
}
